import React from "react";
import { Link } from "gatsby";
import { FiDownload } from "@react-icons/all-files/fi/FiDownload";
import { headerHeight } from "~/utils/variables";
import { PATH } from "~/constant/path";
import { colors } from "~/utils/colors";
import laurelWreathSrc from "~/images/hero-laurel.svg";

const HERO_COPY = {
  leadLines: [
    "制度設計から社員への浸透、紹介促進、運用改善まで。",
    "経験豊富なプロが成功まで徹底伴走します。",
  ],
} as const;

const PROOF_ITEMS = [
  {
    label: "採用支援実績",
    prefix: null as string | null,
    num: "1,000",
    unitAccent: "社",
    unit: "超",
    note: null as string | null,
  },
  {
    label: "導入コスト",
    prefix: "初期費用",
    num: "0",
    unitAccent: "円",
    unit: "",
    note: null as string | null,
  },
  {
    label: "採用全体のリファラル比率",
    prefix: null as string | null,
    num: "20",
    unitAccent: "%",
    unit: "超達成",
    note: "支援企業平均",
  },
] as const;

export const Hero: React.FC = () => {
  return (
    <>
      <section id="hero" className="hero">
        <div className="heroCanvas" aria-hidden="true">
          <div className="heroDots" />
          <div className="heroPhotoStage">
            <img
              className="heroPhoto"
              src="/hero/hero-photo.jpg"
              alt=""
              decoding="async"
              {...{ fetchpriority: "high" }}
            />
          </div>
          <div className="heroVeil" />
          <div className="heroDevices">
            <img className="heroLaptop" src="/hero/hero-laptop.png" alt="" decoding="async" />
            <img className="heroPhone" src="/hero/hero-phone.png" alt="" decoding="async" />
          </div>
        </div>

        <div className="heroBody">
          <h1 className="heroTitle">
            <span className="heroTitleInk">
              <span className="heroLine heroLineFirst">リファラル採用を</span>
              <span className="heroLine">
                <span className="heroEm">
                  <span className="heroMist" aria-hidden="true">
                    <span className="heroMistWide" />
                    <span className="heroMistSoft" />
                    <span className="heroMistCore" />
                  </span>
                  <span className="heroEmWord">成果</span>
                </span>
                につながる
                <br className="heroBreakSp" />
                仕組みに。
              </span>
            </span>
          </h1>
          <p className="heroLead">
            {HERO_COPY.leadLines.map((line) => (
              <span key={line} className="heroLeadLine">
                {line}
              </span>
            ))}
          </p>
          <span className="heroCta">
            <Link to={PATH.REQUEST.to}>
              <span className="heroCtaText">
                <span className="heroCtaSub">サービス内容や事例が分かる</span>
                <span className="heroCtaMain">資料をダウンロード</span>
              </span>
              <FiDownload />
            </Link>
          </span>
        </div>

        <ul className="heroProof">
          {PROOF_ITEMS.map((item) => (
            <li key={item.label} className="heroProofItem">
              <span className="heroProofItemMain">
                <img src={laurelWreathSrc} alt="" className="heroProofLaurel" />
                <div className="heroProofContent">
                  <span className="heroProofLabel">{item.label}</span>
                  <span className="heroProofValue">
                    {item.prefix ? <span className="heroProofValuePrefix">{item.prefix}</span> : null}
                    <span className="heroProofValueNum">{item.num}</span>
                    {item.unitAccent ? (
                      <span className="heroProofValueUnit heroProofValueUnitAccent">{item.unitAccent}</span>
                    ) : null}
                    {item.note ? (
                      <span className="heroProofUnitStack">
                        <span className="heroProofNote">{item.note}</span>
                        {item.unit ? <span className="heroProofValueUnit">{item.unit}</span> : null}
                      </span>
                    ) : item.unit ? (
                      <span className="heroProofValueUnit">{item.unit}</span>
                    ) : null}
                  </span>
                </div>
                <img
                  src={laurelWreathSrc}
                  alt=""
                  className="heroProofLaurel heroProofLaurelRight"
                />
              </span>
            </li>
          ))}
        </ul>
      </section>

      <style jsx>{`
        .hero {
          --hero-ink: #423f3c;
          --hero-mute: #e55c00;
          --hero-wash: #fff7ef;
          --hero-wash-mid: #ffebd6;
          --hero-content: min(1120px, calc(100% - clamp(40px, 18.75vw - 80px, 160px)));
          position: relative;
          overflow: hidden;
          isolation: isolate;
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-height: clamp(500px, 58vw, 560px);
          margin-top: ${headerHeight.desktop}px;
          padding: clamp(22px, 7vw, 34px) 0;
          background: linear-gradient(160deg, #fff7ef 0%, #ffebd6 55%, #ffe3c8 100%);
        }
        .heroCanvas {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }
        .heroDots {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(rgba(236, 127, 24, 0.18) 1px, transparent 1px);
          background-size: 26px 26px;
          mask-image: radial-gradient(780px 560px at 80% 6%, #000 0%, transparent 72%);
        }
        .heroPhotoStage {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
          opacity: 0.78;
        }
        .heroPhoto {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: 86% 28%;
          -webkit-mask-image: linear-gradient(
            90deg,
            transparent 0%,
            rgba(0, 0, 0, 0.08) 10%,
            rgba(0, 0, 0, 0.38) 22%,
            rgba(0, 0, 0, 0.82) 38%,
            #000 52%,
            #000 100%
          );
          mask-image: linear-gradient(
            90deg,
            transparent 0%,
            rgba(0, 0, 0, 0.08) 10%,
            rgba(0, 0, 0, 0.38) 22%,
            rgba(0, 0, 0, 0.82) 38%,
            #000 52%,
            #000 100%
          );
        }
        .heroVeil {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            radial-gradient(ellipse 58% 42% at 16% 94%, rgba(255, 247, 239, 0.72) 0%, transparent 68%),
            linear-gradient(
              90deg,
              #fff7ef 0%,
              rgba(255, 247, 239, 0.94) 18%,
              rgba(255, 247, 239, 0.72) 34%,
              rgba(255, 235, 214, 0.38) 50%,
              rgba(255, 235, 214, 0.12) 64%,
              transparent 78%
            );
        }
        .heroDevices {
          position: absolute;
          right: clamp(3%, 5vw, 8%);
          bottom: clamp(2px, 0.8%, 12px);
          left: auto;
          top: auto;
          width: min(40vw, 420px);
          z-index: 1;
          pointer-events: none;
          overflow: visible;
        }
        .heroDevices::after {
          content: "";
          position: absolute;
          left: 8%;
          right: 2%;
          bottom: -2%;
          height: 24%;
          z-index: 0;
          pointer-events: none;
          background: radial-gradient(
            ellipse 72% 65% at 50% 55%,
            rgba(50, 32, 18, 0.2) 0%,
            rgba(50, 32, 18, 0.08) 42%,
            transparent 74%
          );
          filter: blur(12px);
        }
        .heroLaptop {
          position: relative;
          z-index: 1;
          display: block;
          width: 100%;
          height: auto;
          filter: sepia(0.08) saturate(0.92) brightness(0.99)
            drop-shadow(0 12px 20px rgba(40, 28, 18, 0.18));
        }
        .heroPhone {
          position: absolute;
          z-index: 2;
          left: 6%;
          bottom: 5%;
          height: 54%;
          width: auto;
          filter: sepia(0.08) saturate(0.92) brightness(0.99)
            drop-shadow(0 10px 16px rgba(40, 28, 18, 0.16));
        }
        .heroBody {
          position: relative;
          z-index: 1;
          width: var(--hero-content);
          margin: 0 auto;
          padding: clamp(16px, 2vw, 24px) 0 clamp(20px, 2.4vw, 32px);
        }
        .heroTitle {
          position: relative;
          overflow: visible;
          margin: 0 0 clamp(12px, 1.8vw, 20px);
          max-width: min(16.5em, 100%);
          font-size: clamp(28px, 4.2vw, 53px);
          font-weight: bold;
          letter-spacing: 1px;
          line-height: 1.5;
          color: ${colors.defaultText};
        }
        .heroTitleInk {
          position: relative;
          z-index: 1;
        }
        .heroLine {
          display: block;
        }
        .heroBreakSp {
          display: none;
        }
        .heroEm {
          position: relative;
          display: inline-block;
          color: #e55c00;
          font-weight: bold;
        }
        .heroMist {
          position: absolute;
          left: 50%;
          top: 50%;
          z-index: -1;
          width: 0;
          height: 0;
          pointer-events: none;
        }
        .heroMistWide,
        .heroMistSoft,
        .heroMistCore {
          position: absolute;
          pointer-events: none;
        }
        .heroMistWide {
          left: -13em;
          top: -9em;
          width: 26em;
          height: 18em;
          background: radial-gradient(
            ellipse 70% 58% at 50% 50%,
            rgba(255, 200, 150, 0.4) 0%,
            rgba(255, 235, 214, 0.15) 50%,
            transparent 74%
          );
          filter: blur(1.23em);
        }
        .heroMistSoft {
          left: -8em;
          top: -6.5em;
          width: 16em;
          height: 13em;
          background: radial-gradient(
            ellipse 60% 65% at 50% 50%,
            rgba(236, 127, 24, 0.22) 0%,
            rgba(255, 200, 150, 0.1) 52%,
            transparent 76%
          );
          filter: blur(1em);
        }
        .heroMistCore {
          left: -4.75em;
          top: -4.1em;
          width: 9.5em;
          height: 8.2em;
          background: radial-gradient(
            54% 58% at 50% 50%,
            rgba(236, 127, 24, 0.32) 0%,
            rgba(236, 85, 63, 0.16) 40%,
            rgba(236, 127, 24, 0.05) 68%,
            transparent 86%
          );
          filter: blur(0.81em);
        }
        .heroEmWord {
          position: relative;
          z-index: 1;
        }
        .heroLead {
          position: relative;
          z-index: 1;
          width: fit-content;
          margin: 0 0 clamp(16px, 2.4vw, 28px);
          max-width: min(42em, 100%);
          font-size: clamp(15px, 0.35vw + 14px, 18px);
          font-weight: 400;
          letter-spacing: 0.04em;
          line-height: 1.9;
          color: ${colors.defaultText};
        }
        .heroLeadLine {
          display: block;
        }
        .heroCta {
          position: relative;
          z-index: 2;
          display: inline-flex;
        }
        .heroCta :global(a) {
          display: inline-flex;
          align-items: center;
          gap: clamp(12px, 1.4vw, 18px);
          min-height: clamp(56px, 5.2vw, 68px);
          padding: 14px clamp(22px, 2.8vw, 34px) 14px clamp(24px, 3vw, 36px);
          border-radius: 999px;
          color: ${colors.white};
          background-image: linear-gradient(90deg, #ec7f18 0%, #ec553f 100%);
          background-color: ${colors.brand};
          box-shadow: 0 10px 28px rgba(236, 100, 40, 0.22);
          text-decoration: none;
          transition: transform 0.25s ease, box-shadow 0.25s ease, filter 0.25s ease;
        }
        .heroCta :global(a:hover) {
          transform: translateY(-4px);
          filter: brightness(1.05);
          box-shadow: 0 14px 28px rgba(236, 100, 40, 0.4);
        }
        .heroCtaText {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          line-height: 1.2;
          text-align: center;
        }
        .heroCtaSub {
          font-size: clamp(11px, 0.3vw + 10px, 13px);
          font-weight: 500;
          letter-spacing: 0.04em;
          opacity: 0.95;
        }
        .heroCtaMain {
          font-size: clamp(18px, 0.5vw + 15px, 22px);
          font-weight: 700;
          letter-spacing: 0.06em;
        }
        .heroCta :global(a svg) {
          flex: none;
          width: clamp(22px, 2vw, 26px);
          height: clamp(22px, 2vw, 26px);
          stroke-width: 2.2;
        }
        .heroProof {
          position: relative;
          z-index: 1;
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-start;
          gap: clamp(10px, 3vw, 44px);
          width: var(--hero-content);
          margin: clamp(16px, 1.6vw, 20px) auto 0;
          padding: 0;
          list-style: none;
        }
        .heroProofItem {
          position: relative;
          display: flex;
          flex: none;
        }
        .heroProofItem::before {
          content: "";
          position: absolute;
          inset: -18px -36px;
          z-index: 0;
          pointer-events: none;
          background: radial-gradient(
            ellipse 90% 95% at 50% 50%,
            rgba(255, 247, 239, 0.94) 0%,
            rgba(255, 247, 239, 0.62) 46%,
            transparent 76%
          );
          filter: blur(16px);
        }
        .heroProofItemMain {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          gap: clamp(3px, 0.5vw, 6px);
        }
        .heroProofLaurel {
          width: clamp(22px, 3.6vw, 31px);
          height: clamp(48px, 7.8vw, 67px);
          flex: none;
        }
        .heroProofLaurelRight {
          transform: scaleX(-1);
        }
        .heroProofContent {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 0;
          height: clamp(56px, 8vw, 70px);
          gap: clamp(5px, 0.9vw, 9px);
        }
        .heroProofLabel {
          font-size: clamp(10.5px, 0.7vw + 8px, 11.5px);
          font-weight: 700;
          letter-spacing: 0.14em;
          color: var(--hero-mute);
          line-height: 1;
        }
        .heroProofValue {
          display: flex;
          align-items: flex-end;
          height: clamp(26px, 3.8vw, 34px);
          gap: 0;
          color: #423f3c;
          font-size: clamp(14px, 1.1vw + 10px, 17px);
          font-weight: 500;
          line-height: 1;
          white-space: nowrap;
        }
        .heroProofValue > * {
          line-height: 1;
        }
        .heroProofValuePrefix {
          font-size: clamp(14px, 1.2vw + 10px, 18px);
          font-weight: 700;
          margin-right: 3px;
          color: #423f3c;
          line-height: 1;
        }
        .heroProofValueNum {
          font-size: clamp(26px, 2vw + 19px, 34px);
          font-weight: 700;
          letter-spacing: -0.02em;
          color: #e55c00;
          line-height: 1;
        }
        .heroProofValueUnit {
          font-size: clamp(14px, 1.2vw + 10px, 18px);
          font-weight: 700;
          color: #423f3c;
          margin-left: 1px;
          line-height: 1;
        }
        .heroProofValueUnitAccent {
          color: #e55c00;
          margin-left: 1px;
        }
        .heroProofUnitStack {
          display: inline-flex;
          flex-direction: column;
          align-items: flex-end;
          justify-content: flex-end;
          gap: 2px;
          margin-left: 1px;
          height: 100%;
        }
        .heroProofUnitStack .heroProofValueUnit {
          margin-left: 0;
        }
        .heroProofNote {
          font-size: clamp(8px, 0.35vw + 6.5px, 9px);
          font-weight: 500;
          letter-spacing: 0.02em;
          color: #6b7280;
          line-height: 1.2;
          white-space: nowrap;
        }
        .heroProofItem:nth-child(3) .heroProofLabel {
          font-size: clamp(9.5px, 0.55vw + 7.5px, 10.5px);
          letter-spacing: 0.06em;
        }

        @media (min-width: 1441px) {
          .hero {
            min-height: clamp(560px, 22vw + 130px, 780px);
          }
        }
        @media (min-width: 1201px) {
          .heroPhoto {
            inset: auto;
            top: 0;
            right: -56px;
            bottom: 0;
            left: auto;
            width: auto;
            min-width: min(calc(50% + 288px), calc(25% + 700px));
            height: 100%;
            max-width: none;
            object-fit: cover;
            object-position: 100% 10%;
          }
        }
        @media (max-width: 1200px) {
          .heroPhoto {
            object-position: 58% 28%;
          }
          .heroMistWide {
            background: radial-gradient(
              ellipse 70% 58% at 50% 50%,
              rgba(255, 200, 150, 0.48) 0%,
              rgba(255, 235, 214, 0.18) 50%,
              transparent 74%
            );
          }
          .heroMistSoft {
            background: radial-gradient(
              ellipse 60% 65% at 50% 50%,
              rgba(236, 127, 24, 0.28) 0%,
              rgba(255, 200, 150, 0.12) 52%,
              transparent 76%
            );
          }
          .heroMistCore {
            background: radial-gradient(
              54% 58% at 50% 50%,
              rgba(236, 127, 24, 0.4) 0%,
              rgba(236, 85, 63, 0.2) 40%,
              rgba(236, 127, 24, 0.07) 68%,
              transparent 86%
            );
          }
        }
        @media (max-width: 767px) {
          .hero {
            margin-top: ${headerHeight.mobile}px;
            min-height: auto;
            padding: 28px 0 36px;
          }
          .heroDevices {
            display: none;
          }
          .heroBody {
            text-align: left;
            padding: 8px 0 16px;
          }
          .heroTitle {
            /* 「リファラル採用を」(8文字) が1行で収まるサイズ。余白を少し多めに */
            font-size: clamp(30px, calc((100vw - 64px) / 8.4), 48px);
            letter-spacing: 1.4px;
            max-width: 100%;
          }
          .heroLineFirst {
            white-space: nowrap;
          }
          .heroBreakSp {
            display: block;
          }
          .heroCta :global(a) {
            width: 100%;
            max-width: 360px;
            justify-content: center;
          }
          .heroLead::before {
            content: "";
            position: absolute;
            inset: -10px -20px;
            z-index: -1;
            pointer-events: none;
            background: radial-gradient(
              ellipse 78% 85% at 40% 50%,
              rgba(255, 247, 239, 0.92) 0%,
              rgba(255, 247, 239, 0.55) 48%,
              transparent 74%
            );
            filter: blur(14px);
          }
        }
      `}</style>
    </>
  );
};
